import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PetType } from '@spike/model';
import { fetchOrganizationServicesInfoThunk, MarketplaceStatus } from '@spike/marketplace-action';
import { OverFullWindow, Spinner } from 'components/UI';
import { MultiStepForm } from 'components/UI/V3/Forms/MultiStepForm/MultiStepForm';
import SuccessScreen from 'components/UI/V3/SuccessScreen';
import ExitWithoutSavingModal from 'components/UI/V3/Modals/ExitWithoutSavingModal';
import useNonInitialEffect from '@versiondos/hooks';
import {
    createEmptyWalkingService,
    Service,
    ServicePricing,
    WalkingMetadata,
    SessionBookingType,
    StaffMember,
    MetadataDuration,
    SESSION_BOOKING_TYPES
} from 'model/Service';
import { useMasterData, useMarketplace, useApiClientWrapper } from 'hooks';
import { saveBulkServicesThunk } from 'actions/services/ServicesActions';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import { ServicesStatus } from 'reducers/services/ServicesState';
import { RootState } from 'store';
import { WalkingPetTypeStep } from './Steps/PetType/WalkingPetTypeStep';
import { WalkingDetailsStep } from './Steps/Details/WalkingDetailsStep';
import { WalkingPricingStep } from './Steps/Pricing/WalkingPricingStep';
import { WalkingStaffStep } from './Steps/Staff/WalkingStaffStep';
import styles from './WalkingForm.module.scss';

interface WalkingFormProps {
    mode: 'add' | 'edit';
    service?: Service;
    onClose: () => void;
}

interface WalkingDetailsFormData {
    name: string;
    description?: string;
    durationHours: number;
    durationMinutes: number;
    bookingType: string;
    maxPets: number;
    inHomeService: boolean;
    olbEnabled: boolean;
    color?: string;
    forms?: import('model/Forms').AssociatedForm[];
}

export const WalkingForm: React.FC<WalkingFormProps> = ({ mode, service, onClose }) => {
    const dispatch = useDispatch();
    const masterData = useMasterData();
    const marketplace = useMarketplace();
    const apiClientWrapper = useApiClientWrapper();
    const { organizationServicesInfo } = useOrganizationMarketplaces();
    const fetchPendingRef = useRef(false);

    const servicesStatus = useSelector<RootState, ServicesStatus>(state => state.services.status);
    const marketplaceStatus = useSelector<RootState, MarketplaceStatus>(state => state.marketplace.status);

    const [formStep, setFormStep] = useState(0);
    const [isSuccess, setIsSuccess] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingOrganizationServices, setIsLoadingOrganizationServices] = useState(true);
    const [isEditingSpecificStep, setIsEditingSpecificStep] = useState(false);
    const [showExitPrompt, setShowExitPrompt] = useState(false);

    const [walkingService, setWalkingService] = useState<Service>(() => {
        if (mode === 'edit' && service) {
            return service;
        }
        return createEmptyWalkingService(masterData, marketplace);
    });

    useEffect(() => {
        setIsLoadingOrganizationServices(true);
        fetchPendingRef.current = false;
        dispatch(fetchOrganizationServicesInfoThunk(apiClientWrapper));
        // Fetch once on mount; apiClientWrapper is recreated every render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (marketplaceStatus === MarketplaceStatus.FetchingOrganizationServicesInfo) {
            fetchPendingRef.current = true;
        }

        if (fetchPendingRef.current && marketplaceStatus === MarketplaceStatus.FetchOrganizationServicesInfoSuccess) {
            setIsLoadingOrganizationServices(false);
            fetchPendingRef.current = false;
        }

        if (fetchPendingRef.current && marketplaceStatus === MarketplaceStatus.Error) {
            setIsLoadingOrganizationServices(false);
            fetchPendingRef.current = false;
        }
    }, [marketplaceStatus]);

    useNonInitialEffect(() => {
        switch (servicesStatus) {
            case ServicesStatus.SaveSuccess:
            case ServicesStatus.SaveBulkSuccess:
                setIsSaving(false);
                setIsSuccess(true);
                break;
            case ServicesStatus.Error:
                setIsSaving(false);
                setIsSuccess(false);
                break;
        }
    }, [servicesStatus]);

    const handlePetTypeStep = (petTypes: Array<PetType>, marketplaces?: Service['marketplaces']) => {
        setWalkingService(prev => ({
            ...prev,
            petTypes,
            marketplaces: marketplaces ?? prev.marketplaces
        }));
        setFormStep(prev => prev + 1);
    };

    const handleDetailsStep = (details: WalkingDetailsFormData) => {
        // Private ignores max_pets (capacity is always 1), so the group value is kept and comes back
        // when the service is switched to Group again instead of resetting every session's capacity.
        const maxPets = details.maxPets;
        const updatedMetadata = {
            hours: details.durationHours,
            minutes: details.durationMinutes,
            booking_type: details.bookingType,
            max_pets: maxPets,
            in_home_service: details.inHomeService
        };

        setWalkingService(prev => ({
            ...prev,
            name: details.name,
            description: details.description || '',
            olbEnabled: details.olbEnabled,
            forms: details.forms || [],
            color: details.color || '',
            walkingData: {
                hours: details.durationHours,
                minutes: details.durationMinutes,
                bookingType: details.bookingType as SessionBookingType,
                maxPets,
                inHomeService: details.inHomeService
            } as WalkingMetadata,
            duration: {
                metadata: JSON.stringify(updatedMetadata)
            } as MetadataDuration
        }));
        setFormStep(prev => prev + 1);
    };

    const handlePricingStep = (pricing: ServicePricing, marketplaces: Service['marketplaces']) => {
        setWalkingService(prev => ({
            ...prev,
            pricing,
            marketplaces
        }));
        setFormStep(prev => prev + 1);
    };

    const handleStaffStep = async (staff: Array<StaffMember>, marketplaces: Service['marketplaces']) => {
        try {
            setIsSaving(true);

            const finalService: Service = {
                ...walkingService,
                staff,
                marketplaces
            };

            await dispatch(saveBulkServicesThunk(finalService));
        } catch (error) {
            setIsSaving(false);
        }
    };

    const editStepHandler = (step: number) => {
        setFormStep(step);
        setIsEditingSpecificStep(true);
    };

    const getActiveStep = () => {
        if (mode === 'edit' && !isEditingSpecificStep) {
            return 4;
        }
        return formStep;
    };

    const walkingDetailsFormData: WalkingDetailsFormData = useMemo(
        () => ({
            name: walkingService.name,
            description: walkingService.description,
            durationHours: walkingService.walkingData?.hours ?? 1,
            durationMinutes: walkingService.walkingData?.minutes ?? 0,
            bookingType: walkingService.walkingData?.bookingType || SESSION_BOOKING_TYPES.PRIVATE_SESSION,
            maxPets: walkingService.walkingData?.maxPets || 1,
            inHomeService: walkingService.walkingData?.inHomeService || false,
            olbEnabled: walkingService.olbEnabled,
            color: walkingService.color || undefined,
            forms: walkingService.forms || []
        }),
        [
            walkingService.name,
            walkingService.description,
            walkingService.walkingData,
            walkingService.olbEnabled,
            walkingService.color,
            walkingService.forms
        ]
    );

    const title = mode === 'edit' ? `Editing ${walkingService.name}` : 'Adding a Walking Service';
    const successTitle = mode === 'edit' ? 'Updated Successfully' : 'Good Job';
    const successMessage = (
        <p className={styles.successDescription}>
            <b>{walkingService.name}</b> was {mode === 'edit' ? 'updated' : 'added'} successfully.
        </p>
    );

    if (isSuccess) {
        return (
            <OverFullWindow
                showMobileLogo
                title={title}
                onClose={onClose}
            >
                <SuccessScreen
                    title={successTitle}
                    message={successMessage}
                    buttonLabel="Go to Walking Services"
                    onClick={onClose}
                />
            </OverFullWindow>
        );
    }

    return (
        <OverFullWindow
            showMobileLogo={false}
            title={title}
            onClose={() => setShowExitPrompt(true)}
            headerClassName={styles.walkingFormHeader}
        >
            {showExitPrompt && (
                <ExitWithoutSavingModal
                    onDiscard={onClose}
                    onKeepEditing={() => setShowExitPrompt(false)}
                />
            )}

            {isLoadingOrganizationServices ? (
                <div className={styles.loadingContainer}>
                    <Spinner />
                </div>
            ) : (
                <div className={styles.container}>
                    <MultiStepForm
                        steps={[
                            {
                                title: 'Pet Type and Locations',
                                description: 'Select pet categories for this service',
                                component: (
                                    <WalkingPetTypeStep
                                        selectedPetTypes={walkingService.petTypes}
                                        marketplaces={walkingService.marketplaces}
                                        serviceId={walkingService.id}
                                        onNext={handlePetTypeStep}
                                    />
                                )
                            },
                            {
                                title: 'Walking Service Detail',
                                component: (
                                    <WalkingDetailsStep
                                        formData={walkingDetailsFormData}
                                        serviceId={walkingService.id}
                                        onNext={handleDetailsStep}
                                    />
                                )
                            },
                            {
                                title: 'Pricing',
                                description: 'Set up pricing and taxes for this service',
                                component: (
                                    <WalkingPricingStep
                                        selectedPetTypes={walkingService.petTypes}
                                        marketplaces={walkingService.marketplaces}
                                        existingService={mode === 'edit' ? service : undefined}
                                        onNext={handlePricingStep}
                                    />
                                )
                            },
                            {
                                title: 'Staff',
                                component: (
                                    <WalkingStaffStep
                                        marketplaces={walkingService.marketplaces}
                                        organizationServicesInfo={organizationServicesInfo}
                                        onComplete={handleStaffStep}
                                        isLoading={isSaving}
                                    />
                                )
                            }
                        ]}
                        activeStep={getActiveStep()}
                        onEdit={editStepHandler}
                        flexibleNavigation={mode === 'edit'}
                    />
                </div>
            )}
        </OverFullWindow>
    );
};

export default WalkingForm;
